import { ChartBarIcon, FireIcon, CakeIcon } from '@heroicons/react/24/outline'

/**
 * WeeklySummaryWidget Component
 *
 * Displays weekly nutrition summary with top foods and quick stats
 */
export default function WeeklySummaryWidget({
  weekStart,
  weekEnd,
  avgDailyCalories = 0,
  totalWeeklyCalories = 0,
  totalWeeklyProtein = 0,
  totalWeeklyCarbs = 0,
  totalWeeklyFat = 0,
  goalAdherence = 0,
  mealsLogged = 0,
  totalMealsExpected = 21,
  topFoods = []
}) {
  // Format date range
  const formatDateRange = (start, end) => {
    const startDate = new Date(start)
    const endDate = new Date(end)
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
    return `${formatter.format(startDate)} - ${formatter.format(endDate)}`
  }

  // Get adherence color
  const getAdherenceColor = (percent) => {
    if (percent >= 80) return 'text-green-600'
    if (percent >= 60) return 'text-amber-600'
    return 'text-red-600'
  }

  return (
    <div className="bg-surface-container rounded-lg shadow-md border border-gray-200 p-5 sticky top-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Weekly Summary</h3>
        <ChartBarIcon className="h-6 w-6 text-indigo-600" />
      </div>

      {/* Date Range */}
      <p className="text-sm text-gray-600 mb-4">
        {formatDateRange(weekStart, weekEnd)}
      </p>

      {/* Stats */}
      <div className="space-y-3 mb-5">
        {/* Avg Daily Calories */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <FireIcon className="h-5 w-5 text-orange-500 mr-2" />
            <span className="text-sm text-gray-700">Avg Daily:</span>
          </div>
          <span className="text-sm font-bold text-gray-900">
            {Math.round(avgDailyCalories)} cal
          </span>
        </div>

        {/* Goal Adherence */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-indigo-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-gray-700">Goal Adherence:</span>
          </div>
          <span className={`text-sm font-bold ${getAdherenceColor(goalAdherence)}`}>
            {Math.round(goalAdherence)}%
          </span>
        </div>

        {/* Meals Logged */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CakeIcon className="h-5 w-5 text-pink-500 mr-2" />
            <span className="text-sm text-gray-700">Meals Logged:</span>
          </div>
          <span className="text-sm font-bold text-gray-900">
            {mealsLogged}/{totalMealsExpected}
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 my-4"></div>

      {/* Weekly Totals */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Weekly Totals:</h4>
        <div className="space-y-2 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-3">
          {/* Total Calories */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700 font-medium">Total Calories:</span>
            <span className="text-sm font-bold text-indigo-600">
              {Math.round(totalWeeklyCalories).toLocaleString()} cal
            </span>
          </div>

          {/* Macronutrients */}
          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-indigo-200">
            <div className="text-center">
              <div className="text-xs text-gray-600">Protein</div>
              <div className="text-sm font-bold text-blue-600">
                {Math.round(totalWeeklyProtein)}g
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-600">Carbs</div>
              <div className="text-sm font-bold text-amber-600">
                {Math.round(totalWeeklyCarbs)}g
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-600">Fat</div>
              <div className="text-sm font-bold text-orange-600">
                {Math.round(totalWeeklyFat)}g
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 my-4"></div>

      {/* Top Foods */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Top Foods This Week:</h4>
        {topFoods.length > 0 ? (
          <ol className="space-y-2">
            {topFoods.slice(0, 5).map((food, index) => (
              <li key={index} className="text-sm flex items-start">
                <span className="text-indigo-600 font-medium mr-2">{index + 1}.</span>
                <div className="flex-grow">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-800">{food.name}</span>
                    <span className="text-gray-500 text-xs ml-2">({food.frequency}x)</span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-gray-500 italic">No food data yet</p>
        )}
      </div>
    </div>
  )
}
